import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { BillingHistory } from '@/types/license';

interface GenerateInvoiceData {
  clientLicenseId: string;
  amount: number;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  dueDate: string;
  notes?: string;
}

interface UpdatePaymentData {
  invoiceId: string;
  status: 'paid' | 'overdue' | 'cancelled';
  paidDate?: string;
  paymentMethod?: string;
  notes?: string;
}

export const useBillingOperations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const generateInvoiceMutation = useMutation({
    mutationFn: async (invoiceData: GenerateInvoiceData) => {
      const { data, error } = await supabase
        .from('billing_history')
        .insert([{
          client_license_id: invoiceData.clientLicenseId,
          amount: invoiceData.amount,
          currency: 'BRL',
          billing_period_start: invoiceData.billingPeriodStart,
          billing_period_end: invoiceData.billingPeriodEnd,
          invoice_date: new Date().toISOString(),
          due_date: invoiceData.dueDate,
          status: 'pending' as const,
          notes: invoiceData.notes,
        }])
        .select(`
          *,
          client_license:client_licenses (
            id,
            client:clients (id, name),
            license_plan:license_plans (id, name)
          )
        `)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-history'] });
      toast({
        title: "Fatura gerada",
        description: "Fatura gerada com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error generating invoice:', error);
      toast({
        title: "Erro ao gerar fatura",
        description: "Não foi possível gerar a fatura.",
        variant: "destructive",
      });
    }
  });

  const updatePaymentStatusMutation = useMutation({
    mutationFn: async (paymentData: UpdatePaymentData) => {
      const updateData: any = {
        status: paymentData.status,
        payment_method: paymentData.paymentMethod,
        notes: paymentData.notes,
      };

      if (paymentData.status === 'paid' && paymentData.paidDate) {
        updateData.paid_date = paymentData.paidDate;
      }

      const { data, error } = await supabase
        .from('billing_history')
        .update(updateData)
        .eq('id', paymentData.invoiceId)
        .select(`
          *,
          client_license:client_licenses (
            id,
            client:clients (id, name),
            license_plan:license_plans (id, name)
          )
        `)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-history'] });
      toast({
        title: "Status atualizado",
        description: "Status de pagamento atualizado com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error updating payment status:', error);
      toast({
        title: "Erro ao atualizar status",
        description: "Não foi possível atualizar o status de pagamento.",
        variant: "destructive",
      });
    }
  });

  const sendPaymentReminderMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      // Aqui você pode integrar com um serviço de email
      // Por enquanto, apenas simula o envio
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Atualizar a data do último lembrete
      const { data, error } = await supabase
        .from('billing_history')
        .update({ 
          notes: `Lembrete enviado em ${new Date().toLocaleString('pt-BR')}` 
        })
        .eq('id', invoiceId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-history'] });
      toast({
        title: "Lembrete enviado",
        description: "Lembrete de pagamento enviado ao cliente.",
      });
    },
    onError: (error: any) => {
      console.error('Error sending reminder:', error);
      toast({
        title: "Erro ao enviar lembrete",
        description: "Não foi possível enviar o lembrete.",
        variant: "destructive",
      });
    }
  });

  const generateRecurringInvoicesMutation = useMutation({
    mutationFn: async () => {
      // Buscar licenças ativas que precisam de cobrança
      const { data: activeLicenses, error: fetchError } = await supabase
        .from('client_licenses')
        .select(`
          *,
          license_plan:license_plans(*),
          client:clients(id, name)
        `)
        .eq('status', 'active');

      if (fetchError) throw fetchError;

      const today = new Date();
      const invoicesToGenerate = [];

      for (const license of activeLicenses) {
        const startDate = new Date(license.start_date);
        const dayOfMonth = startDate.getDate();
        
        // Verificar se é o dia de cobrança
        if (today.getDate() === dayOfMonth) {
          const billingPeriodStart = new Date(today);
          const billingPeriodEnd = new Date(today);
          
          if (license.billing_cycle === 'monthly') {
            billingPeriodEnd.setMonth(billingPeriodEnd.getMonth() + 1);
          } else {
            billingPeriodEnd.setFullYear(billingPeriodEnd.getFullYear() + 1);
          }

          const dueDate = new Date(today);
          dueDate.setDate(dueDate.getDate() + 7); // 7 dias para pagamento

          const amount = license.billing_cycle === 'yearly' 
            ? license.license_plan.price_yearly || license.license_plan.price_monthly * 12
            : license.license_plan.price_monthly;

          invoicesToGenerate.push({
            client_license_id: license.id,
            amount,
            currency: 'BRL',
            billing_period_start: billingPeriodStart.toISOString(),
            billing_period_end: billingPeriodEnd.toISOString(),
            invoice_date: today.toISOString(),
            due_date: dueDate.toISOString(),
            status: 'pending' as const,
            notes: `Cobrança automática - ${license.billing_cycle === 'monthly' ? 'Mensal' : 'Anual'}`,
          });
        }
      }

      if (invoicesToGenerate.length > 0) {
        const { data, error } = await supabase
          .from('billing_history')
          .insert(invoicesToGenerate)
          .select();

        if (error) throw error;
        return data;
      }

      return [];
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['billing-history'] });
      if (data.length > 0) {
        toast({
          title: "Faturas geradas",
          description: `${data.length} fatura(s) gerada(s) automaticamente.`,
        });
      }
    },
    onError: (error: any) => {
      console.error('Error generating recurring invoices:', error);
      toast({
        title: "Erro na cobrança automática",
        description: "Erro ao gerar faturas recorrentes.",
        variant: "destructive",
      });
    }
  });

  const syncBillingFromStripeMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('sync-billing-history');
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['billing-history'] });
      toast({
        title: "Sincronização concluída",
        description: `${data?.synced || 0} fatura(s) sincronizada(s) do Stripe.`,
      });
    },
    onError: (error: any) => {
      console.error('Error syncing from Stripe:', error);
      toast({
        title: "Erro na sincronização",
        description: "Não foi possível sincronizar com o Stripe.",
        variant: "destructive",
      });
    }
  });

  return {
    generateInvoice: generateInvoiceMutation.mutate,
    updatePaymentStatus: updatePaymentStatusMutation.mutate,
    sendPaymentReminder: sendPaymentReminderMutation.mutate,
    generateRecurringInvoices: generateRecurringInvoicesMutation.mutate,
    syncBillingFromStripe: syncBillingFromStripeMutation.mutate,
    isGeneratingInvoice: generateInvoiceMutation.isPending,
    isUpdatingPayment: updatePaymentStatusMutation.isPending,
    isSendingReminder: sendPaymentReminderMutation.isPending,
    isGeneratingRecurring: generateRecurringInvoicesMutation.isPending,
    isSyncingFromStripe: syncBillingFromStripeMutation.isPending,
  };
};